import React, { useState, useEffect, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import API_URL from '../config';

function Login() {
  const navigate = useNavigate();
  const [blurBg, setBlurBg] = useState(false);
  const [showHi, setShowHi] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false);

  const [theme, setTheme] = useState(() => {
    const isDefaultSet = localStorage.getItem("vjconnect_theme_default") === "true";
    const lastTheme = localStorage.getItem("vjconnect_theme") || "dark";
    if (isDefaultSet) {
      return lastTheme;
    } else {
      // Shuffling mode: alternate the theme on load
      const nextTheme = lastTheme === "dark" ? "light" : "dark";
      localStorage.setItem("vjconnect_theme", nextTheme);
      return nextTheme;
    }
  });

  const cartoonRef = useRef(null);
  const eyeRefs = useRef([]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("vjconnect_theme", theme);
  }, [theme]);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "vjconnect_theme" && e.newValue) {
        setTheme(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const getRedirectTarget = (role) => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const paramRedirect = searchParams.get("redirect");
      const storedRedirect = localStorage.getItem("vjlink_redirect");
      const target = paramRedirect || storedRedirect;
      if (target && target.startsWith("/") && target !== "/") {
        localStorage.removeItem("vjlink_redirect");
        return target;
      }
    } catch (e) {}
    return role === "faculty" ? "/faculty/home" : "/home";
  };

  useEffect(() => {
    const existingUser = localStorage.getItem("vjconnect_user");
    if (existingUser) {
      try {
        const parsedUser = JSON.parse(existingUser);
        if (parsedUser && parsedUser.email) {
          setUserData(parsedUser);
          setShowSuccess(true);
          
          setTimeout(() => {
            navigate(getRedirectTarget(parsedUser.role));
          }, 1200);
        }
      } catch (e) {
        // failed to parse, stay on login
      }
    }
  }, [navigate]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      html, body, #root { margin: 0; padding: 0; }
      * { box-sizing: border-box; }

      .login-gradient-bg {
        display: flex;
        min-height: 100vh;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #0f1022 0%, #1a1f3a 40%, #2b2440 100%);
        transition: filter 0.6s, background 0.5s;
        position: relative;
        overflow: hidden;
      }

      /* light mode gradient override */
      [data-theme="light"] .login-gradient-bg {
        background: linear-gradient(135deg, #eef2f7 0%, #dbeafe 45%, #e0f2fe 100%);
      }

      /* subtle stars layer */
      .login-gradient-bg::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image:
          radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.9) 0%, transparent 50%),
          radial-gradient(1px 1px at 60px 80px, rgba(255,255,255,0.7) 0%, transparent 50%),
          radial-gradient(1px 1px at 120px 40px, rgba(255,255,255,0.6) 0%, transparent 50%);
        background-repeat: repeat;
        background-size: 200px 120px;
        opacity: 0.75;
        transform: translate3d(var(--mouse-x, 0), var(--mouse-y, 0), 0);
        transition: transform 0.12s linear;
        pointer-events: none;
        z-index: 2;
        mix-blend-mode: screen;
      }

      [data-theme="light"] .login-gradient-bg::before {
        background-image:
          radial-gradient(1.5px 1.5px at 20px 30px, rgba(15,23,42,0.15) 0%, transparent 50%),
          radial-gradient(1.5px 1.5px at 60px 80px, rgba(15,23,42,0.1) 0%, transparent 50%),
          radial-gradient(1.5px 1.5px at 120px 40px, rgba(15,23,42,0.08) 0%, transparent 50%);
        opacity: 0.85;
        mix-blend-mode: multiply;
      }

      /* colored nebula / galaxies */
      .login-gradient-bg::after {
        content: '';
        position: absolute;
        width: 1400px;
        height: 1400px;
        right: -300px;
        top: -600px;
        background:
          radial-gradient(closest-side at 30% 30%, rgba(120,119,198,0.18), transparent 20%),
          radial-gradient(closest-side at 70% 70%, rgba(255,119,198,0.12), transparent 10%);
        filter: blur(80px) saturate(120%);
        transform: translate3d(calc(var(--mouse-x, 0) * -0.08), calc(var(--mouse-y, 0) * -0.06), 0) rotate(8deg);
        transition: transform 0.12s linear;
        pointer-events: none;
        z-index: 1;
      }

      [data-theme="light"] .login-gradient-bg::after {
        background:
          radial-gradient(closest-side at 30% 30%, rgba(99,102,241,0.08), transparent 20%),
          radial-gradient(closest-side at 70% 70%, rgba(236,72,153,0.06), transparent 10%);
      }

      /* floating decorative elements */
      .floating-planet {
        position: absolute;
        border-radius: 50%;
        pointer-events: none;
        mix-blend-mode: screen;
        opacity: 0.95;
      }

      [data-theme="light"] .floating-planet {
        mix-blend-mode: multiply;
        opacity: 0.7;
      }

      .floating-planet.p1 {
        width: 120px;
        height: 120px;
        left: 6%;
        top: 12%;
        background: radial-gradient(circle at 30% 30%, #ffb3d6, #8a2be2);
        animation: floaty 6s ease-in-out infinite;
        z-index: 3;
      }

      [data-theme="light"] .floating-planet.p1 {
        background: radial-gradient(circle at 30% 30%, #fbcfe8, #c084fc);
      }

      .floating-planet.p2 {
        width: 80px;
        height: 80px;
        right: 8%;
        bottom: 18%;
        background: radial-gradient(circle at 20% 30%, #7fe0f7, #2b5cff);
        animation: floaty 7s ease-in-out infinite;
        z-index: 3;
      }

      [data-theme="light"] .floating-planet.p2 {
        background: radial-gradient(circle at 20% 30%, #bae6fd, #60a5fa);
      }

      .floating-planet.p3 {
        width: 48px;
        height: 48px;
        left: 40%;
        bottom: 24%;
        background: radial-gradient(circle at 30% 30%, #ffd67a, #ff6b6b);
        animation: floaty 5.5s ease-in-out infinite;
        z-index: 3;
      }

      [data-theme="light"] .floating-planet.p3 {
        background: radial-gradient(circle at 30% 30%, #fef08a, #fca5a5);
      }

      .floating-planet.p4 {
        width: 36px;
        height: 36px;
        right: 20%;
        top: 28%;
        background: radial-gradient(circle at 30% 30%, rgba(180,255,200,0.9), rgba(60,180,200,0.6));
        animation: floaty 6.5s ease-in-out infinite;
        z-index: 3;
      }

      [data-theme="light"] .floating-planet.p4 {
        background: radial-gradient(circle at 30% 30%, rgba(187,247,208,0.7), rgba(147,197,253,0.4));
      }

      @keyframes floaty {
        0% { transform: translateY(0) translateX(0); }
        50% { transform: translateY(-12px) translateX(6px); }
        100% { transform: translateY(0) translateX(0); }
      }

      .blur-bg {
        filter: blur(14px) brightness(0.85) saturate(1.08);
        background: linear-gradient(120deg, #13293aff 0%, #2b1f4d 60%, #1b2b3bff 100%);
      }

      [data-theme="light"] .blur-bg {
        background: linear-gradient(120deg, #e2e8f0 0%, #cbd5e1 100%);
      }

      .login-card {
        background: rgba(255,255,255,0.06);
        padding: 28px;
        border-radius: 16px;
        box-shadow: 0 12px 40px rgba(11,12,20,0.6), inset 0 1px 0 rgba(255,255,255,0.02);
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 92%;
        max-width: 520px;
        backdrop-filter: blur(8px) saturate(120%);
        border: 1px solid rgba(255,255,255,0.06);
        z-index: 30;
        position: relative;
      }

      [data-theme="light"] .login-card {
        background: rgba(255, 255, 255, 0.45);
        box-shadow: 0 12px 40px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.4);
        border: 1px solid rgba(255,255,255,0.5);
        backdrop-filter: blur(12px) saturate(140%);
      }

      /* Cartoon / mascot styles */
      .login-layout {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        width: 100%;
        max-width: 1100px;
        padding: 24px;
        z-index: 30;
        justify-content: center; /* center the cards */
      }

      .mascot-area {
        flex: 1 1 380px;
        min-height: 420px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        pointer-events: none;
      }

      .mascot-stage {
        /* Match login card size/padding exactly */
        width: 520px;
        max-width: calc(92%);
        background: rgba(255,255,255,0.06);
        border-radius: 16px;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        gap: 8px;
        position: relative;
        overflow: visible;
        padding: 28px;
        flex: 0 0 420px; /* match login card width */
        min-height: 420px; /* fixed height to match login card */
        box-shadow: 0 12px 40px rgba(11,12,20,0.6), inset 0 1px 0 rgba(255,255,255,0.02);
        border: 1px solid rgba(255,255,255,0.06);
        backdrop-filter: blur(8px) saturate(120%);
      }

      [data-theme="light"] .mascot-stage {
        background: rgba(255, 255, 255, 0.45);
        box-shadow: 0 12px 40px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.4);
        border: 1px solid rgba(255,255,255,0.5);
        backdrop-filter: blur(12px) saturate(140%);
      }

      /* Flat vector-style mascots */
      .mascot {
        /* use relative positioning so flex layout controls placement */
        position: relative;
        transform-origin: bottom center;
        /* smooth transform for wiggle */
        transition: transform 240ms cubic-bezier(.2,.9,.2,1), box-shadow 240ms;
        box-shadow: 0 8px 18px rgba(0,0,0,0.08);
        flex: 0 0 auto;
      }

      /* active wiggle uses transform; JS toggles this class on proximity */
      .mascot.active-wiggle {
        transform: translateY(-6px) rotate(-3deg) skewX(-2deg);
        box-shadow: 0 18px 36px rgba(0,0,0,0.25);
      }

      /* Orange semi-circle mascot */
      .mascot-orange {
        width: 160px; /* increased size */
        height: 80px;
        background: #FF7B3B;
        border-radius: 200px 200px 0 0;
      }
      .mascot-orange .eye.left { left: 30px; top: 20px; }
      .mascot-orange .eye.right { right: 30px; top: 20px; }
      .mascot-orange .mouth {
        position: absolute;
        width: 40px;
        height: 12px;
        border: 2px solid #333;
        border-radius: 0 0 20px 20px;
        border-top: 0;
        bottom: 15px;
        left: 50%;
        transform: translateX(-50%);
      }

      /* Purple tall rectangle mascot */
      .mascot-purple {
        width: 90px; /* increased */
        height: 180px;
        background: #6B4EE6;
        border-radius: 10px;
        z-index: 1; /* sit slightly behind others */
      }
      .mascot-purple .eye.left { left: 20px; top: 28px; }
      .mascot-purple .eye.right { right: 20px; top: 28px; }

      /* Add small smiling mouth for purple mascot */
      .mascot-purple .mouth {
        position: absolute;
        width: 28px;
        height: 10px;
        border-radius: 0 0 16px 16px;
        border: 2px solid #222;
        border-top: 0;
        bottom: 18px;
        left: 50%;
        transform: translateX(-50%);
      }

      /* Black rectangle mascot */
      .mascot-black {
        width: 70px;
        height: 140px;
        background: #2A2A2A;
        border-radius: 8px;
      }
      .mascot-black .eye.left { left: 16px; top: 26px; }
      .mascot-black .eye.right { right: 16px; top: 26px; }

      /* Add small straight mouth for black mascot */
      .mascot-black .mouth {
        position: absolute;
        width: 18px;
        height: 3px;
        background: #111;
        bottom: 18px;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 2px;
      }

      /* Yellow rounded rectangle mascot */
      .mascot-yellow {
        width: 70px;
        height: 130px;
        background: #FFD12E;
        border-radius: 10px;
      }
      .mascot-yellow .eye.left { left: 50%; top: 28px; transform: translateX(-50%); }
      .mascot-yellow .mouth {
        position: absolute;
        width: 20px;
        height: 8px;
        border-radius: 0 0 12px 12px;
        border: 2px solid #222;
        border-top: 0;
        bottom: 28px;
        left: 50%;
        transform: translateX(-50%);
      }

      /* Common eye styles */
      .eye {
        position: absolute;
        width: 16px; /* slightly larger for bigger mascots */
        height: 16px;
        background: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        box-shadow: 0 2px 6px rgba(0,0,0,0.18);
      }

      .pupil {
        width: 8px; /* larger pupil for bigger eyes */
        height: 8px;
        background: #222;
        border-radius: 50%;
        transform: translate(0px, 0px);
        transition: transform 0.04s linear;
      }

      /* cloud-like transparent speech bubble */
      .speech {
        position: absolute;
        left: 50%;
        transform: translateX(-50%) translateY(6px);
        top: -36px;
        /* slightly more visible cloud but still transparent */
        background: rgba(255,255,255,0.14);
        color: rgba(10,10,10,0.95);
        padding: 12px 18px;
        border-radius: 28px;
        font-weight: 800;
        box-shadow: 0 8px 28px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.02);
        backdrop-filter: blur(6px) saturate(120%);
        border: 1px solid rgba(255,255,255,0.08);
        opacity: 0;
        pointer-events: none;
        min-width: 96px;
        text-align: center;
      }

      [data-theme="light"] .speech {
        background: rgba(255, 255, 255, 0.85);
        color: #0f172a;
        box-shadow: 0 8px 28px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.8);
        border: 1px solid rgba(15,23,42,0.08);
      }

      .speech::after {
        content: '';
        position: absolute;
        left: 52%;
        bottom: -10px;
        width: 10px;
        height: 10px;
        background: rgba(255,255,255,0.14);
        border-radius: 50%;
        transform: translateX(-50%);
        box-shadow: 18px 6px 0 rgba(255,255,255,0.08), 36px 12px 0 rgba(255,255,255,0.06);
      }

      [data-theme="light"] .speech::after {
        background: rgba(255, 255, 255, 0.85);
        box-shadow: 18px 6px 0 rgba(255,255,255,0.65), 36px 12px 0 rgba(255,255,255,0.5);
      }

      .speech.show {
        animation: popUp 600ms cubic-bezier(.2,.9,.2,1) both;
        opacity: 1;
      }
      
      /* --- Expression variants --- */
      /* Eyes: closed/squint variant */
      .eye.closed { height:10px; width:30px; border-radius:10px; background:transparent; box-shadow:none; border-bottom:3px solid #fff; }
      .eye.squint { height:12px; width:26px; border-radius:10px; background:transparent; box-shadow:none; border-bottom:2px solid #fff; }
      .eye.wide { width:34px; height:34px; }
      .pupil.hidden { opacity:0; }

      /* Mouth expressions */
      .mouth.happy { border:0; height:12px; width:38px; border-bottom:4px solid #222; border-radius:0 0 22px 22px; background:transparent; }
      .mouth.sad { border:0; height:12px; width:38px; border-top:4px solid #222; border-radius:22px 22px 0 0; transform:translateY(6px); background:transparent; }
      .mouth.line { height:6px; width:26px; border-radius:4px; background:#222; border:0; }
      .mouth.open { height:18px; width:22px; background:#222; border-radius:10px; border:0; }

      /* Body expressions */
      .character.expr-lean { transform: translateY(-8px) rotate(-4deg) skewX(-2deg) scaleY(1.02); }
      .character.expr-shrink { transform: translateY(2px) scale(0.96); }
      .character.expr-bounce { animation: bounce 480ms ease both; }
      @keyframes bounce { 0%{ transform: translateY(0);}50%{ transform: translateY(-10px);}100%{ transform: translateY(0);} }

      /* per-character default tweaks to help expressions read well */
      #orange-blob .mouth { bottom:12px; }
      #purple-rect .mouth { bottom:24px; height:10px; }
      #black-rect .mouth { bottom:18px; height:6px; width:18px; }
      #yellow-rect .mouth { bottom:28px; height:6px; width:20px; }

      @keyframes popUp {
        0% { transform: translateX(-50%) translateY(20px) scale(0.95); opacity: 0; }
        60% { transform: translateX(-50%) translateY(-6px) scale(1.02); opacity: 1; }
        100% { transform: translateX(-50%) translateY(0px) scale(1); opacity: 1; }
      }

      /* Responsive adjustments */
      @media (max-width: 880px) {
        .login-layout { flex-direction: column; gap: 0.4rem; padding: 12px 18px; align-items: center; margin-top: -5vh; }
        .mascot-area { min-height: unset; }
        .mascot-stage { 
          width: calc(100% - 36px);
          max-width: 420px;
          min-height: 170px;
          flex: 0 0 auto;
          justify-content: center;
          padding: 8px 18px;
          margin: 0 18px;
        }
        /* Show only orange and purple mascots on mobile */
        .mascot { transform: none; }
        /* show only first two mascots and center them */
        .mascot-black, .mascot-yellow { display: none; }
        .mascot-orange, .mascot-purple { width: 140px; height: auto; }
        .mascot-orange { width: 140px; height: 70px; }
        .mascot-purple { width: 80px; height: 160px; }
        .eye { width: 12px; height: 12px; }
        .pupil { width: 6px; height: 6px; }
        .speech { top: -22px; font-size: 15px; padding: 10px 14px; background: rgba(255,255,255,0.16); }
        /* match login card width with mascot stage */
        .login-card {
          margin: 0 18px;
          width: calc(100% - 36px);
          max-width: 420px;
        }
      }

      .login-card img {
        width: 72px;
        height: auto;
      }

      .login-card h2 {
        color: rgba(255,255,255,0.95);
        text-align: center;
      }

      [data-theme="light"] .login-card h2 {
        color: #1e293b;
      }

      @media (min-width: 900px) {
        .login-card {
          padding: 36px;
          max-width: 600px;
        }
      }

      @media (max-width: 900px) {
        .floating-planet.p1 { width: 72px; height: 72px; left: 4%; top: 8%; opacity: 0.9; }
        .floating-planet.p2 { width: 56px; height: 56px; right: 6%; bottom: 14%; opacity: 0.95; }
        .floating-planet.p3 { width: 40px; height: 40px; left: 45%; bottom: 20%; opacity: 0.95; }
        .floating-planet.p4 { width: 28px; height: 28px; right: 18%; top: 22%; opacity: 0.95; }
        .login-card { padding: 18px; border-radius: 12px; }
        .login-logo { width: 220px !important; margin-bottom: 8px !important; }
        .login-card h2 { font-size: 1.1rem; margin-bottom: 16px !important; }
      }

      .theme-toggle-btn:hover {
        background: rgba(255, 255, 255, 0.15) !important;
        transform: scale(1.08);
      }
      [data-theme="light"] .theme-toggle-btn:hover {
        background: rgba(15, 23, 42, 0.08) !important;
      }
    `;
    document.head.appendChild(style);

    // show initial cloud 'Hii' on mount
    setShowHi(true);
    const hiTimer = setTimeout(() => setShowHi(false), 1600);

    // Parallax background movement
    const handleMove = (e) => {
      const x = (e.clientX - window.innerWidth / 2) * 0.02 + "px";
      const y = (e.clientY - window.innerHeight / 2) * 0.02 + "px";
      document.documentElement.style.setProperty("--mouse-x", x);
      document.documentElement.style.setProperty("--mouse-y", y);
    };
    window.addEventListener("mousemove", handleMove);

    /* Eye & expression tracking using mousemove on window */
    let blinkTimer = null;
    const clickHandlers = [];

    const handleEyeMove = (e) => {
      const mx = e.clientX;
      const my = e.clientY;
      const root = cartoonRef.current;
      if (!root) return;

      const chars = Array.from(root.querySelectorAll('.character'));
      chars.forEach((ch) => {
        const eyes = Array.from(ch.querySelectorAll('.eye'));
        eyes.forEach((eye) => {
          const eyeRect = eye.getBoundingClientRect();
          const ex = eyeRect.left + eyeRect.width / 2;
          const ey = eyeRect.top + eyeRect.height / 2;
          const dx = mx - ex;
          const dy = my - ey;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const eyeRadius = Math.max(4, (Math.min(eyeRect.width, eyeRect.height) / 2) - 4);
          const max = Math.max(6, Math.min(eyeRadius, 12));
          const nx = (dx / dist) * Math.min(max, dist / 6);
          const ny = (dy / dist) * Math.min(max, dist / 6);
          const pupil = eye.querySelector('.pupil');
          if (pupil) pupil.style.transform = `translate(${nx}px, ${ny}px)`;
        });

        const r = ch.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = mx - cx, dy = my - cy;
        const d = Math.hypot(dx, dy);
        const THRESH = Math.max(100, r.width * 1.0);

        if (d < THRESH) {
          ch.classList.add('active-wiggle');
        } else {
          ch.classList.remove('active-wiggle');
        }

        ch.classList.remove('expr-lean', 'expr-shrink', 'expr-bounce');
        ch.querySelectorAll('.eye').forEach(e => e.classList.remove('closed', 'squint', 'wide'));
        ch.querySelectorAll('.pupil').forEach(p => p.classList.remove('hidden'));
        ch.querySelectorAll('.mouth').forEach(m => m.className = 'mouth');

        if (d < THRESH * 0.35) {
          if (ch.id === 'orange-blob') { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('happy')); ch.classList.add('expr-lean'); }
          else if (ch.id === 'purple-rect') { ch.querySelectorAll('.eye').forEach(e => e.classList.add('wide')); ch.querySelectorAll('.mouth').forEach(m => m.classList.add('sad')); ch.classList.add('expr-shrink'); }
          else if (ch.id === 'black-rect') { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('line')); ch.classList.add('expr-shrink'); }
          else if (ch.id === 'yellow-rect') { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('open')); ch.classList.add('expr-bounce'); }
        } else if (d < THRESH * 0.7) {
          if (ch.id === 'orange-blob') { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('happy')); }
          else if (ch.id === 'purple-rect') { ch.querySelectorAll('.eye').forEach(e => e.classList.add('squint')); }
          else if (ch.id === 'black-rect') { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('line')); }
          else if (ch.id === 'yellow-rect') { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('line')); }
        }
      });
    };

    window.addEventListener('mousemove', handleEyeMove);
    if (cartoonRef.current) cartoonRef.current.addEventListener('mousemove', handleEyeMove);

    function scheduleBlink() {
      blinkTimer && clearTimeout(blinkTimer);
      blinkTimer = setTimeout(() => {
        const root = cartoonRef.current; if (!root) return scheduleBlink();
        const chars = Array.from(root.querySelectorAll('.character'));
        if (chars.length === 0) return scheduleBlink();
        const idx = Math.floor(Math.random() * chars.length);
        const ch = chars[idx];
        const eyes = Array.from(ch.querySelectorAll('.eye'));
        eyes.forEach(e => e.classList.add('closed'));
        ch.querySelectorAll('.pupil').forEach(p => p.classList.add('hidden'));
        setTimeout(() => { eyes.forEach(e => e.classList.remove('closed')); ch.querySelectorAll('.pupil').forEach(p => p.classList.remove('hidden')); }, 180 + Math.random() * 220);
        scheduleBlink();
      }, 1800 + Math.random() * 2400);
    }
    scheduleBlink();

    if (cartoonRef.current) {
      const chars = Array.from(cartoonRef.current.querySelectorAll('.character'));
      chars.forEach((ch, i) => {
        const handler = (ev) => {
          ev.stopPropagation();
          const cur = Number(ch.getAttribute('data-expr') || 0);
          const next = (cur + 1) % 4;
          ch.setAttribute('data-expr', String(next));
          ch.querySelectorAll('.eye').forEach(e => e.classList.remove('closed', 'squint', 'wide'));
          ch.querySelectorAll('.pupil').forEach(p => p.classList.remove('hidden'));
          ch.querySelectorAll('.mouth').forEach(m => m.className = 'mouth');
          ch.classList.remove('expr-lean', 'expr-shrink', 'expr-bounce');
          if (next === 1) { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('happy')); ch.classList.add('expr-lean'); }
          else if (next === 2) { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('sad')); ch.classList.add('expr-shrink'); }
          else if (next === 3) { ch.querySelectorAll('.mouth').forEach(m => m.classList.add('open')); ch.classList.add('expr-bounce'); setTimeout(() => ch.classList.remove('expr-bounce'), 500); }
        };
        ch.addEventListener('click', handler);
        clickHandlers.push({ ch, handler });
      });
    }

    return () => {
      document.head.removeChild(style);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener('mousemove', handleEyeMove);
      if (cartoonRef.current) cartoonRef.current.removeEventListener('mousemove', handleEyeMove);
      try { clearTimeout(blinkTimer); } catch (e) { }
      try { clickHandlers.forEach(({ ch, handler }) => ch.removeEventListener('click', handler)); } catch (e) { }
      clearTimeout(hiTimer);
    };
  }, []);

  const handleSuccess = async (credentialResponse) => {
    setShowHi(true);
    setBlurBg(true);

    try {
      const res = await fetch(`${API_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      const data = await res.json();
      if (res.ok) {
        const toUpperName = (s) =>
          !s ? s : String(s).trim().replace(/\s+/g, " ").toUpperCase();
        const userToStore = { name: toUpperName(data.name), email: data.email, role: data.role };
        if (data.rollNo) userToStore.rollNo = data.rollNo;
        localStorage.setItem("vjconnect_user", JSON.stringify(userToStore));
        try {
          const eventsKey = 'vjconnect_login_events';
          const existing = JSON.parse(localStorage.getItem(eventsKey) || '[]');
          existing.push({ email: data.email, name: toUpperName(data.name), time: new Date().toISOString() });
          localStorage.setItem(eventsKey, JSON.stringify(existing));
        } catch (e) {
          console.warn('Failed to record login event', e);
        }

        setShowHi(false);
        setBlurBg(false);
        setUserData(data);
        setShowSuccess(true);

        setTimeout(() => {
          navigate(getRedirectTarget(data.role));
        }, 1200);
      } else {
        alert("Login failed: " + (data.detail || "Unknown error"));
        setBlurBg(false);
        setShowHi(false);
      }
    } catch (err) {
      console.error(err);
      alert("Login failed: network error");
      setBlurBg(false);
      setShowHi(false);
    }
  };

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    setShowDefaultPrompt(true);
  };

  return (
    <div className={`login-gradient-bg${blurBg ? " blur-bg" : ""}`}>
      {/* Dynamic Backdrop Blur Overlay when default prompt is open */}
      <AnimatePresence>
        {showDefaultPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backdropFilter: 'blur(8px)',
              background: theme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)',
              zIndex: 998,
              pointerEvents: 'auto'
            }}
            onClick={() => {
              // Click on backdrop acts as "No" (Dismisses the prompt without setting permanent default)
              localStorage.setItem("vjconnect_theme_default", "false");
              setShowDefaultPrompt(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* Floating Theme Control Group (sits above the backdrop blur) */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
        <button
          className="theme-toggle-btn"
          onClick={toggleTheme}
          style={{
            background: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)',
            border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(15, 23, 42, 0.08)',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease',
          }}
          title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {theme === 'dark' ? <Sun size={20} color="#fcd34d" /> : <Moon size={20} color="#475569" />}
        </button>

        <AnimatePresence>
          {showDefaultPrompt && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              style={{
                background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.75)',
                color: theme === 'dark' ? 'white' : '#0f172a',
                padding: '16px 20px',
                borderRadius: '16px',
                boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(20px)',
                border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(15, 23, 42, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '240px',
                alignItems: 'stretch'
              }}
            >
              <div style={{ fontSize: '0.9rem', fontWeight: '800', textAlign: 'center' }}>
                Set default theme?
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    localStorage.setItem("vjconnect_theme_default", "false");
                    setShowDefaultPrompt(false);
                  }}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(15, 23, 42, 0.2)',
                    color: 'inherit',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    transition: 'background 0.2s'
                  }}
                >
                  No
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem("vjconnect_theme_default", "true");
                    setShowDefaultPrompt(false);
                  }}
                  style={{
                    flex: 1,
                    background: 'var(--accent-color, #6B4EE6)',
                    border: 'none',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    transition: 'opacity 0.2s'
                  }}
                >
                  Yes
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Decorative floating planets */}
      <div className="floating-planet p1" aria-hidden="true" />
      <div className="floating-planet p2" aria-hidden="true" />
      <div className="floating-planet p3" aria-hidden="true" />
      <div className="floating-planet p4" aria-hidden="true" />

      <div className="login-layout">
        <div className="mascot-area">
          <div className="mascot-stage" ref={cartoonRef}>
            {/* Orange semi-circle mascot */}
            <div id="orange-blob" className="mascot character mascot-orange">
              <div className="eye left" ref={(el) => (eyeRefs.current[0] = el)}>
                <div className="pupil" />
              </div>
              <div className="eye right" ref={(el) => (eyeRefs.current[1] = el)}>
                <div className="pupil" />
              </div>
              <div className="mouth" />
            </div>

            {/* Purple tall rectangle mascot */}
            <div id="purple-rect" className="mascot character mascot-purple">
              <div className="eye left" ref={(el) => (eyeRefs.current[2] = el)}>
                <div className="pupil" />
              </div>
              <div className="eye right" ref={(el) => (eyeRefs.current[3] = el)}>
                <div className="pupil" />
              </div>
              <div className="mouth" />
            </div>

            {/* Black rectangle mascot */}
            <div id="black-rect" className="mascot character mascot-black">
              <div className="eye left" ref={(el) => (eyeRefs.current[4] = el)}>
                <div className="pupil" />
              </div>
              <div className="eye right" ref={(el) => (eyeRefs.current[5] = el)}>
                <div className="pupil" />
              </div>
              <div className="mouth" />
            </div>

            {/* Yellow rounded rectangle mascot */}
            <div id="yellow-rect" className="mascot character mascot-yellow">
              <div className="eye left" ref={(el) => (eyeRefs.current[6] = el)}>
                <div className="pupil" />
              </div>
              <div className="mouth" />
            </div>

            <div className={`speech ${showHi ? 'show' : ''}`} aria-live="polite">
              {showHi ? '👋 Hii there!' : ''}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', width: '100%' }}>
          <h1 style={{ color: theme === 'light' ? '#0f172a' : 'white', fontSize: 30, marginBottom: 6, fontWeight: 900 }}>VJ LINK</h1>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="login-card" style={{ flex: '0 0 420px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginBottom: '16px' }}>
                <img
                  src={theme === 'light' ? "/VNRVJIETlogo-light_mode.png" : "/vnrvjiet-logo.png"}
                  alt="VNRVJIET Logo"
                  style={{ width: 250, borderRadius: 12 }}
                />
                <div style={{ width: 1, height: 40, background: theme === 'light' ? 'rgba(15,23,42,0.15)' : 'rgba(255,255,255,0.15)' }} />
                <img
                  src="/vjlink-logo-v2.png"
                  alt="VJLink Logo"
                  style={{ width: 100, borderRadius: 12 }}
                />
              </div>
              <h2 style={{ marginBottom: 24 }}>
                Come connect with your college community
              </h2>
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={() => alert("Login Failed")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Success Animation Overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'linear-gradient(135deg, #eef2f7 0%, #dbeafe 100%)' : 'linear-gradient(135deg, #0f1022 0%, #1a1f3a 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{
                scale: [0, 1.2, 1],
                rotate: 0,
                filter: ["blur(10px)", "blur(0px)"]
              }}
              transition={{
                duration: 0.45,
                ease: "backOut",
                times: [0, 0.7, 1]
              }}
              style={{ marginBottom: 40 }}
            >
              <img
                src="/vjlink-logo-v2.png"
                alt="Logo"
                style={{ width: 180, borderRadius: 24, boxShadow: theme === 'light' ? '0 20px 50px rgba(15,23,42,0.15)' : '0 20px 50px rgba(0,0,0,0.5)' }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              style={{ textAlign: 'center' }}
            >
              <h1 style={{ color: theme === 'light' ? '#0f172a' : 'white', fontSize: '2.5rem', fontWeight: 900, marginBottom: 8 }}>
                Welcome Back!
              </h1>
              <p style={{ color: theme === 'light' ? '#475569' : 'rgba(255,255,255,0.7)', fontSize: '1.2rem' }}>
                {userData?.name}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Login;
